<template>
    <div class="general-modal">
        <BaseModal :padding="isMobileOrTablet ? MODAL_SCROLLABLE_PADDING_MOBILE : MODAL_SCROLLABLE_PADDING_DESKTOP"
                   maxWidth="500px"
                   maxHeight="600px"
                   :isClosable="false"
                   hasScrollTopShadow
                   hasScrollBottomShadow>
            <template #header>
                <div class="general-modal__header-container">
                    <h2 class="general-modal__title">
                        <Translate translation-key="quests-dashboard.guilds.popularGuildsToFollow"
                                   fallback="Popular Guilds to Follow" />
                    </h2>
                    <div class="general-modal__subtitle">
                        <Translate translation-key="quests-dashboard.guilds.beforeYouStart"
                                   :replacements="{number: REQUIRED_GUILDS}"
                                   fallback="Before you start, follow at least :number popular Guilds!" />
                    </div>
                </div>
                <div class="modal-search">
                    <SearchFilter v-model="searchInput"
                                  :placeholder="getTrans('quests-dashboard.guilds.searchGuilds', 'Search Guilds')" />
                </div>
            </template>
            <template #content>
                <div class="general-modal__content general-modal__content--alt">
                    <GuildsSuggestionsModalContent />
                </div>
            </template>
            <template #footer>
                <div class="general-modal__actions general-modal__actions--alt">
                    <span class="general-modal__action general-modal__action--confirm">
                        <BaseButton :disabled="!isSelectedGuildsCountValid || isLoading"
                                    :loading="isLoading"
                                    @click="handleConfirm">
                            <Translate v-if="!isSelectedGuildsCountValid"
                                       translationKey="quests-dashboard.followtAtLeast"
                                       :replacements="{number: selectedGuildsCountRemaining}"
                                       fallback="Follow :number more"/>
                            <span v-else>
                                <Translate translationKey="quests-dashboard.letsGo"
                                           fallback="Let's Go" /> 🚀
                            </span>
                        </BaseButton>
                    </span>
                </div>
            </template>
        </BaseModal>
    </div>
</template>

<script setup lang="ts">
    import {
        computed, onUnmounted, ref, watch,
    } from 'vue';
    import BaseModal from '@/js/components/BaseModal.vue';
    import { useModalActive } from '@/js/composables/useModalActive';
    import GuildsSuggestionsModalContent from '@/js/quests/dashboard/pages/guilds/components/GuildsSuggestionsModalContent.vue';
    import { useDeviceSize } from '@/js/composables/useDeviceSize';
    import { useGuildsStore } from '@/js/store/quests/dashboard/GuildsStore';
    import { useUserStore } from '@/js/store/quests/dashboard/UserStore';
    import { followGuilds } from '@/js/quests/dashboard/services/guildFollowApi';
    import SearchFilter from '@/js/quests/dashboard/components/filters/components/SearchFilter.vue';
    import { debounce } from '@/js/pages/helpers/debounce';
    import { getTrans } from '@/js/helpers/getTrans';
    import BaseButton from '@/js/components/BaseButton.vue';
    import {
        MODAL_SCROLLABLE_PADDING_DESKTOP,
        MODAL_SCROLLABLE_PADDING_MOBILE,
    } from '@/js/quests/global/constants/modal';

    const emit = defineEmits<{(e: 'close'): void; }>();

    const { isMobileOrTablet } = useDeviceSize();
    const guildsStore = useGuildsStore();
    const userStore = useUserStore();

    const { fetchSidebarFollowedGuilds } = userStore;

    const DEBOUNCE_DELAY = 500;
    const REQUIRED_GUILDS = 3;
    const { fetchSuggestedGuilds, resetSuggestedGuildsCurrentPage, resetSuggestedGuilds } = guildsStore;
    const selectedGuildsCountRemaining = computed(() => REQUIRED_GUILDS - guildsStore.suggestedGuilds.selected.length);
    const isSelectedGuildsCountValid = computed(() => guildsStore.suggestedGuilds.selected.length >= REQUIRED_GUILDS);
    const isLoading = ref(false);

    const searchInput = computed({
        get: () => guildsStore.suggestedGuilds.search,
        set: (newValue) => {
            guildsStore.suggestedGuilds.search = newValue;
        },
    });

    const handleConfirm = async () => {
        isLoading.value = true;
        try {
            await followGuilds(guildsStore.suggestedGuilds.selected);
            await fetchSidebarFollowedGuilds();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        } finally {
            isLoading.value = false;
            emit('close');
        }
    };

    const debouncedFetchData = debounce(async () => {
        resetSuggestedGuildsCurrentPage();
        await fetchSuggestedGuilds();
    }, DEBOUNCE_DELAY);

    watch(() => guildsStore.suggestedGuilds.search, () => {
        debouncedFetchData();
    });

    useModalActive();

    onUnmounted(() => {
        resetSuggestedGuilds();
    });
</script>

<style scoped lang="scss">
@use "@/sass/partials/breakpoints" as breakpoints;
@use "@/js/quests/dashboard/sass/mixins/_modal" as modalMixins;

.general-modal {
    @include modalMixins.modal;

    &__content--alt {
        padding-right: 24px;
        padding-left: 24px;
        margin-bottom: 0;

        @media (max-width: breakpoints.$md-breakpoint-max-width) {
            padding-right: 16px;
            padding-left: 16px;
        }
    }

    &__actions {
        justify-content: center;
    }

    &__action {
        &--confirm {
            @media (min-width: breakpoints.$md-breakpoint) {
                flex: none;
            }

            button {
                width: 200px;

                @media (max-width: breakpoints.$md-breakpoint-max-width) {
                    width: 100%;
                }
            }
        }
    }
}

.modal-search {
    padding: 0 16px 16px 16px;

    .dropdown-search {
        padding: 9px 14px;
    }
}
</style>
